import { vec2 } from 'gl-matrix';
import { useCallback, useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import {
    currentDoseState,
    doseDataState,
    doseScaleState,
} from '~/state/atoms/dose/doseAtoms';
import { dosePredictionTaskState } from '~/state/atoms/dose/dosePredictionTaskAtom';
import { environmentState } from '~/state/atoms/engineAtoms';
import transformDoseMatrixToDoseData from '~/utils/transforms/doseMatrixTransforms';
import { getHighestDoseQuantity } from '~/utils/transforms/doseTargetsTransforms';

/**
 * Hook to manage dose-related state in Recoil.
 *
 * This hook initializes and manages dose data based on either:
 * 1. A provided `DoseMatrix` instance (parameter `doseInstance`), or
 * 2. The globally managed `currentDoseState` if `doseInstance` is not provided.
 *
 * @param {DoseMatrix} [doseInstance] - Optional dose instance to use for initializing and managing state.
 * @returns {object} - An object containing the dose data (`dose`) and associated Recoil state management.
 *
 * Example usage:
 * 1. With a specific dose instance:
 *    ```
 *    const { dose } = useDose(doseInstance);
 *    ```
 *
 * 2. With the globally managed current dose:
 *    ```
 *    const { dose } = useDose();
 *    ```
 */
export default function useDose(doseInstance?: DoseMatrix) {
    const environment = useRecoilValue(environmentState);
    const currentDose = useRecoilValue(currentDoseState);
    const dosePredictionTask = useRecoilValue(dosePredictionTaskState);
    const activeDose = doseInstance || currentDose;
    const [dose, setDose] = useRecoilState(
        doseDataState(activeDose ? activeDose.dicomUid : 'default')
    );
    const [doseScale, setDoseScale] = useRecoilState(doseScaleState);

    useEffect(() => {
        if (!dose && activeDose) {
            const generated =
                activeDose.dicomDose.manufacturer === 'MVision AI';
            const transformedDoseData = transformDoseMatrixToDoseData(
                activeDose,
                doseScale,
                undefined,
                generated ? dosePredictionTask : undefined
            );
            setDoseScale(1.0);
            setDose({
                ...transformedDoseData,
                normalizationValueGy: activeDose.doseMaxGy,
            });
        }
    }, [activeDose]);

    useEffect(() => {
        if (dose && activeDose) {
            const transformedDoseData = transformDoseMatrixToDoseData(
                activeDose,
                doseScale,
                dose
            );
            if (!dose.generated) {
                setDoseScale(1.0);
                setDose((prev) => ({
                    ...prev,
                    ...transformedDoseData,
                    normalizationValueGy: activeDose.doseMaxGy * doseScale,
                }));
            } else {
                setDose((prev) => ({
                    ...prev,
                    ...transformedDoseData,
                    normalizationValueGy:
                        getHighestDoseQuantity(
                            dose.targets ?? dosePredictionTask?.targets
                        ) ||
                        prev?.normalizationValueGy ||
                        activeDose.doseMaxGy * doseScale,
                }));
            }
            setDoseRange(dose.selectedRange ?? [0.25, 1]);
        }
    }, [activeDose, doseScale, setDose]);

    const setNormalizationValue = useCallback(
        (normalizationValueGy: number) => {
            setDose((prev) =>
                prev ? { ...prev, normalizationValueGy } : prev
            );
        },
        [activeDose, setDose]
    );

    const setDoseRange = useCallback(
        (selectedRange: vec2) => {
            setDose((prev) => (prev ? { ...prev, selectedRange } : prev));
            if (environment) {
                environment.updateSelectedDoseRange(selectedRange);
            }
        },
        [activeDose, setDose]
    );

    return {
        dose,
        setNormalizationValue,
        setDoseRange,
    };
}
